import { useAuth0 } from '@auth0/auth0-react';
import React, { ReactNode, useCallback, useEffect, useMemo } from 'react';
import constVars from '~/constants/constVars';
import { history } from '~/store';
import { AuthContext } from './AuthContext';
import { AuthContextValue } from './types';

export const Auth0AuthProvider = ({
  children,
  hasIsAuthenticatedCookie,
}: {
  children: ReactNode;
  hasIsAuthenticatedCookie: boolean;
}) => {
  const {
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    isAuthenticated,
    isLoading,
    logout: auth0Logout,
    loginWithRedirect,
    user,
  } = useAuth0();

  const logout = useCallback(
    async (options?: any) => {
      await auth0Logout(
        options ?? {
          logoutParams: {
            returnTo: `${globalThis.location.origin}/${constVars.ROUTE_LOGIN}`,
          },
        }
      );
    },
    [auth0Logout]
  );

  const auth = useMemo<AuthContextValue>(
    () => ({
      provider: 'auth0',
      isAuthenticated,
      isLoading,
      user,
      loginWithRedirect,
      logout,
      getAccessTokenSilently,
      getAccessTokenWithPopup,
    }),
    [
      isAuthenticated,
      isLoading,
      user,
      loginWithRedirect,
      logout,
      getAccessTokenSilently,
      getAccessTokenWithPopup,
    ]
  );

  useEffect(() => {
    if (isLoading) return;

    const url = new URL(globalThis.location.href);
    const hasLoginError = url.searchParams.get('error') === 'login_required';

    if (hasLoginError) {
      url.searchParams.delete('error');
      url.searchParams.delete('error_description');
      url.searchParams.delete('state');
      history.replace({ search: url.search });
      return;
    }

    if (!isAuthenticated) {
      if (import.meta.env.MODE === 'internal-prod') {
        // For internal-prod, redirect directly to Auth0 login (same behavior as old withAuthenticationRequired({}))
        loginWithRedirect({
          appState: {
            returnTo: `${globalThis.location.pathname}${globalThis.location.search}`,
          },
        });
      } else if (hasIsAuthenticatedCookie) {
        sessionStorage.setItem(
          'returnTo',
          `${globalThis.location.pathname}${globalThis.location.search}`
        );
        loginWithRedirect({
          authorizationParams: {
            prompt: 'none',
            redirect_uri: globalThis.location.origin,
          },
          appState: {
            returnTo: `${globalThis.location.pathname}${globalThis.location.search}`,
          },
        });
      }
    }
  }, [isLoading, isAuthenticated]);

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};
